import { App, Button } from 'antd';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { FileUploader } from 'components/FileUploader';
import type { OnCompleteStatus } from 'components/FileUploader/Modal/FileUploadModal';
import { ModalActionButtons } from 'components/ui';
import {
  useNotifyInspectionImageUploadErrorMutation,
  useNotifyInspectionImageUploadSuccessMutation,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { useAuthContext } from 'utils/auth';
import { IMAGE_AND_METADATA_UPLOAD_OPTIONS } from 'utils/constants';
import { INGEST_SERVICE_HOSTNAME } from 'utils/env';
import { useGetIngestUploadUrls } from 'utils/ingest';

// TODO: do we need any validation here?
const isValidFilePath = (filename: string) => {
  return !!filename;
};

interface PayloadData {
  recordId?: string;
  bundleId?: string;
  dataType?: string;
  organizationId?: string;
  files?: { fileName: string }[];
  params?: any;
}
interface AeroEnterpriseUploadUploadProps {
  inspectionId: string;
  onComplete: () => void;
  onBack: () => void;
}

export const AeroEnterpriseUpload: React.FunctionComponent<AeroEnterpriseUploadUploadProps> = ({
  inspectionId,
  onComplete: onSuccessCallback,
  onBack,
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { getAuthHeader } = useAuthContext();
  const { customer } = useAccountContext();
  const organizationId = customer?.id;

  const [notifySuccess] = useNotifyInspectionImageUploadSuccessMutation();
  const [notifyError] = useNotifyInspectionImageUploadErrorMutation();
  const { cancelTokenSource, getIngestUploadUrls } = useGetIngestUploadUrls();

  let payloadData: PayloadData = {
    recordId: undefined,
    bundleId: undefined,
    dataType: undefined,
    organizationId: undefined,
    files: undefined,
    params: {},
  };

  const getUploadUrls = async (filePaths: any) => {
    if (!organizationId) {
      return undefined;
    }

    try {
      const { items, payloadData: data } = await getIngestUploadUrls({
        recordId: inspectionId,
        dataType: IMAGE_AND_METADATA_UPLOAD_OPTIONS.AERO_ENTERPRISE,
        files: filePaths || [],
      });
      payloadData = data;

      return items;
    } catch (_error) {
      return undefined;
    }
  };

  let currentFile: number = 0;
  const onFileComplete = async () => {
    currentFile++;
    if (currentFile !== payloadData?.files?.length) {
      return;
    }

    const url = `${INGEST_SERVICE_HOSTNAME}/post/complete?organizationId=${organizationId}`;
    try {
      const reg = new RegExp('^(?!.*events).+json.*');
      const metadataFile = payloadData.files?.find((file) => file.fileName.match(reg));
      if (!metadataFile) {
        throw Error('No Metadata File was found. Please include a details.json file.');
      }

      payloadData = {
        ...payloadData,
        files: [metadataFile],
        params: {
          draftId: inspectionId,
          autoProcess: false,
        },
      };

      await axios.post(url, payloadData, {
        headers: {
          ...(await getAuthHeader()),
          'Content-Type': 'application/json',
        },
      });
    } catch (_error) {
      return undefined;
    }
  };

  const onUploadComplete = () => {
    onSuccessCallback();
    notification.open({
      message: t(
        'inspection.uploadManager.processingNotificationTitle',
        'Inspection images currently processing.'
      ),
      description: t(
        'inspection.uploadManager.processingNotificationDescription',
        'You will receive a notification when your Inspection has been processed.'
      ),
    });
  };

  const onComplete = ({ status }: { status: OnCompleteStatus }) => {
    if (status === 'CANCEL') {
      cancelTokenSource.cancel('Operation canceled by the user.');
      return;
    }
    if (status === 'SUCCESS') {
      onUploadComplete();
      notifySuccess({
        variables: {
          input: {
            inspectionId: inspectionId,
            message: t(
              'inspection.uploadManager.uploadSuccessMessage',
              'Your images and metadata have been successfully uploaded to your Inspection with id "{{inspectionId}}".',
              { inspectionId }
            ),
          },
        },
      });
    }
    if (status === 'ERROR') {
      notifyError({
        variables: {
          input: {
            inspectionId: inspectionId,
            message: t(
              'inspection.uploadManager.uploadErrorMessage',
              'An error occurred while uploading images and metadata to your Inspection with id "{{inspectionId}}".',
              { inspectionId }
            ),
          },
        },
      });
    }
  };

  return (
    <div>
      <p>
        {t(
          'inspection.uploadManager.aeroEnterpriseInstructions',
          'An Aero Enterprise Inspection must have folder in the correct format. Once the folder is uploaded, it will be processed and released automatically. Inspection images and data will not be manually editable after upload.'
        )}
      </p>
      <ModalActionButtons>
        <Button data-testid="inspection-upload-cancel-btn" onClick={onBack}>
          {t('common.back', 'Back')}
        </Button>
        <FileUploader
          id="aero-enterprise-data-upload"
          label={t('inspection.uploadManager.uploadFolder', 'Upload Folder')}
          acceptedFileTypes={'image/jpeg,.cr2,application/json,text/csv,text/plain'}
          multiple={true}
          directory={true}
          validation={isValidFilePath}
          onFileComplete={onFileComplete}
          onComplete={onComplete}
          action={getUploadUrls}
          multipartProps={undefined}
        />
      </ModalActionButtons>
    </div>
  );
};
